import { useEffect, useMemo, useState } from "react";
import { posts, type Post } from "@/mocks/data";

const navItems = [
  { label: "头条聚焦", href: "#top" },
  { label: "最新事件", href: "#latest" },
  { label: "热榜追踪", href: "#rank" },
  { label: "时间线复盘", href: "#timeline" },
  { label: "常见问答", href: "#faq" },
  { label: "关于本站", href: "#about" },
];

function ResultCard({ post, onClose }: { post: Post; onClose: () => void }) {
  return (
    <a
      href="#latest"
      onClick={onClose}
      className="group flex items-center gap-4 rounded-lg border border-background-200 bg-background-50 p-3 transition-colors hover:border-primary-300"
    >
      <img
        src={post.cover}
        alt={post.title}
        className="h-14 w-20 shrink-0 rounded-md object-cover"
      />
      <div className="min-w-0">
        <p className="truncate text-sm font-medium text-foreground-900 group-hover:text-primary-700">
          {post.title}
        </p>
        <p className="mt-1 flex items-center gap-2 text-xs text-foreground-500">
          <span className="inline-flex items-center rounded-full bg-secondary-100 px-2 py-0.5 text-secondary-800">
            {post.category}
          </span>
          <span>{post.tag}</span>
          <span className="inline-flex items-center gap-0.5 text-primary-600">
            <i className="ri-fire-line" /> {post.heat}
          </span>
        </p>
      </div>
    </a>
  );
}

export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [keyword, setKeyword] = useState("");

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const results = useMemo(() => {
    if (!keyword.trim()) return posts.slice(0, 4);
    const k = keyword.trim().toLowerCase();
    return posts
      .filter(
        (p) =>
          p.title.toLowerCase().includes(k) ||
          p.tag.toLowerCase().includes(k) ||
          p.category.toLowerCase().includes(k)
      )
      .slice(0, 6);
  }, [keyword]);

  return (
    <>
      <header
        className={
          scrolled
            ? "fixed inset-x-0 top-0 z-40 border-b border-background-200 bg-background-50/95 backdrop-blur"
            : "sticky top-0 z-40 border-b border-background-200 bg-background-50"
        }
      >
        <div className="mx-auto flex w-full items-center justify-between gap-4 px-4 py-3 md:px-6">
          {/* brand */}
          <a href="#top" className="flex items-center gap-2.5">
            <span className="flex h-9 w-9 items-center justify-center rounded-md bg-primary-500 text-background-50">
              <i className="ri-focus-3-line text-lg" />
            </span>
            <span className="font-heading text-lg font-black tracking-tight text-foreground-950">
              最近黑料
            </span>
            <span className="hidden rounded-full bg-secondary-100 px-2 py-0.5 text-xs text-secondary-700 sm:inline-block">
              持续跟进中
            </span>
          </a>

          {/* desktop nav */}
          <nav className="hidden items-center gap-1 lg:flex">
            {navItems.map((n) => (
              <a
                key={n.href}
                href={n.href}
                className="whitespace-nowrap rounded-md px-3 py-2 text-sm text-foreground-700 transition-colors hover:bg-background-100 hover:text-primary-700"
              >
                {n.label}
              </a>
            ))}
          </nav>

          {/* actions */}
          <div className="flex items-center gap-2">
            <button
              onClick={() => {
                setSearchOpen(true);
                setKeyword("");
              }}
              className="flex items-center gap-2 rounded-md border border-background-300 bg-background-50 px-3 py-2 text-sm text-foreground-500 transition-colors hover:border-primary-300 hover:text-primary-700"
            >
              <i className="ri-search-line text-base" />
              <span className="hidden md:inline">搜索事件</span>
            </button>
            <a
              href="#faq"
              className="hidden whitespace-nowrap items-center gap-1 rounded-md bg-accent-500 px-4 py-2 text-sm font-medium text-background-50 transition-colors hover:bg-accent-600 md:flex"
            >
              <i className="ri-notification-3-line text-base" />
              订阅进展
            </a>
            <button
              onClick={() => setMobileOpen((v) => !v)}
              className="flex h-10 w-10 items-center justify-center rounded-md border border-background-300 text-foreground-700 lg:hidden"
              aria-label="菜单"
            >
              <i className={mobileOpen ? "ri-close-line text-xl" : "ri-menu-line text-xl"} />
            </button>
          </div>
        </div>

        {/* mobile menu */}
        {mobileOpen && (
          <nav className="border-t border-background-200 bg-background-50 px-4 py-3 lg:hidden">
            <div className="flex flex-col gap-1">
              {navItems.map((n) => (
                <a
                  key={n.href}
                  href={n.href}
                  onClick={() => setMobileOpen(false)}
                  className="rounded-md px-3 py-2.5 text-sm text-foreground-800 transition-colors hover:bg-background-100"
                >
                  {n.label}
                </a>
              ))}
            </div>
          </nav>
        )}
      </header>

      {/* search overlay */}
      {searchOpen && (
        <div className="fixed inset-0 z-50 flex items-start justify-center bg-foreground-950/40 px-4 pt-[12vh]" onClick={() => setSearchOpen(false)}>
          <div
            className="w-full max-w-2xl overflow-hidden rounded-xl border border-background-200 bg-background-50"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center gap-3 border-b border-background-200 px-4 py-3">
              <i className="ri-search-line text-xl text-primary-600" />
              <input
                autoFocus
                value={keyword}
                onChange={(e) => setKeyword(e.target.value)}
                placeholder="输入关键词，检索正在跟进的事件…"
                className="w-full bg-transparent text-base text-foreground-900 outline-none placeholder:text-foreground-400"
              />
              <button
                onClick={() => setSearchOpen(false)}
                className="flex h-8 w-8 items-center justify-center rounded-md text-foreground-500 hover:bg-background-100"
                aria-label="关闭"
              >
                <i className="ri-close-line text-xl" />
              </button>
            </div>
            <div className="flex max-h-[52vh] flex-col gap-2 overflow-y-auto p-3">
              {results.length === 0 ? (
                <p className="px-2 py-8 text-center text-sm text-foreground-500">
                  未找到相关事件，换个关键词试试？
                </p>
              ) : (
                results.map((p) => (
                  <ResultCard key={p.id} post={p} onClose={() => setSearchOpen(false)} />
                ))
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
}